import * as pc from 'playcanvas';

import { applyTransform } from './apply-transform';
import { applyLodFloor } from './lod-clamp';
import type { SplatElement } from './scene-types';

/**
 * Load a single splat asset and instantiate it as a GSplat-component entity.
 *
 * The schema's `url` is same-origin-relative to the scene folder; the caller
 * resolves it to an absolute URL before passing it here.
 *
 * Asset registration mirrors the editor's `AssetLoader.loadModel` path: name
 * the asset by id (so debugging tools surface it cleanly), set type='gsplat',
 * and await `ready` / `error` via a Promise.
 */
export async function loadSplat(
    app: pc.AppBase,
    parent: pc.Entity,
    el: SplatElement,
    absoluteUrl: string
): Promise<pc.Entity> {
    const asset = new pc.Asset(`splat:${el.id}`, 'gsplat', { url: absoluteUrl });
    app.assets.add(asset);

    await new Promise<void>((resolve, reject) => {
        asset.once('load', () => resolve());
        asset.once('error', (err: unknown) => {
            reject(new Error(`splat "${el.id}" failed to load: ${String(err)}`));
        });
        app.assets.load(asset);
    });

    const entity = new pc.Entity(`splat:${el.id}`);
    entity.addComponent('gsplat', {
        asset
    });

    // Two render paths in PlayCanvas 2.19:
    //
    //   - Unified mode (default, gsplat.unified === true): required for
    //     LOD-streamed octree assets (.gsplat-octree / lod-meta.json). The
    //     scene's gsplat system manages a shared work buffer across all
    //     splats. Per-instance material access goes through
    //     app.scene.gsplat.material.
    //
    //   - Non-unified mode (gsplat.unified = false): one GSplatInstance per
    //     component, each owning its own material. Required for plain .ply /
    //     .spz / .sog assets — those don't have the texture layout the
    //     unified renderer assumes (would crash with "textureDimensions of
    //     undefined" otherwise) and they don't activate in unified mode for
    //     reasons that need more investigation.
    //
    // We detect which path to use two ways:
    //
    //   1. URL convention (primary): LOD octree bundles are referenced by a
    //      `lod-meta.json` (or `.gsplat-octree`) url. This is the documented
    //      contract and — crucially — survives minification. The class-name
    //      check below does NOT: esbuild renames `GSplatOctreeResource` to
    //      something like `Qw` in the production build, so the string compare
    //      silently fails, we wrongly force `unified = false` on an octree
    //      asset, and the engine crashes with "textureDimensions of undefined".
    //      (`pc.GSplatOctreeResource` is not a public export, so we can't
    //      `instanceof` it either.)
    //   2. Resource constructor name (dev fallback): works in unminified dev
    //      builds and covers any octree asset that doesn't follow the url
    //      convention.
    const looksLikeOctreeUrl = /lod-meta\.json($|[?#])|\.gsplat-octree($|[?#])/i.test(absoluteUrl);
    const resourceCtorName = (asset.resource as { constructor?: { name?: string } } | undefined)?.constructor?.name;
    const isLodOctree = looksLikeOctreeUrl || resourceCtorName === 'GSplatOctreeResource';
    if (!isLodOctree) {
        (entity.gsplat as unknown as { unified: boolean }).unified = false;
        // ?sr=1: hand the splat to SuperSplat's renderer instead of the engine's
        const sr = (app as unknown as { splatRenderer?: { addEntity(e: pc.Entity, a: pc.Asset): Promise<void> } | null }).splatRenderer;
        if (sr) {
            await sr.addEntity(entity, asset);
        }
    }

    // The boot clamp is set before any splat exists (mount.ts); the engine
    // keeps it per component now, so each new splat picks it up here.
    applyLodFloor(app, entity);

    applyTransform(entity, el.transform);

    // Optional post-transform flip. Lets a scene that exports an upside-down
    // splat get flipped without rewriting the editor export pipeline. Schema
    // extension: `splat.flip = "z180" | "x180" | "y180"`.
    const flip = (el as unknown as { flip?: string }).flip;
    if (flip === 'z180') entity.rotateLocal(0, 0, 180);
    else if (flip === 'x180') entity.rotateLocal(180, 0, 0);
    else if (flip === 'y180') entity.rotateLocal(0, 180, 0);
    else if (flip === 'x90') entity.rotateLocal(90, 0, 0);
    else if (flip === 'x-90') entity.rotateLocal(-90, 0, 0);
    else if (flip === 'z90') entity.rotateLocal(0, 0, 90);
    else if (flip === 'z-90') entity.rotateLocal(0, 0, -90);

    parent.addChild(entity);

    // No keyboard orientation cycler here any more: the old dev-tool one
    // (X / Z / F / 0 rotated the splat) shipped to every visitor, so pressing
    // F on a client's tour turned it upside down (14/09/2026). Orientation is
    // set in the editor, or permanently with `splat.flip` above.

    return entity;
}
